
export type OffsetDateTime = string;
export type LocalDateTime = string;

export enum ConsignmentStatus {
    UNASSIGNED = "UNASSIGNED",
    SCHEDULED = "SCHEDULED",
    ON_ROUTE = "ON_ROUTE",
    PICKED_UP = "PICKED_UP",
    AT_DC = "AT_DC",
    ASSIGNED = "ASSIGNED",
    STARTED = "STARTED",
    COMPLETED = "COMPLETED",
    CANCELLED = "CANCELLED"
}

export interface PublicApiVehicleSummaryResponse {
    typeDescription: string;
    registration: string;
}

export interface PublicApiDriverSummaryResponse {
    firstName: string;
    lastNameInitial: string;
    hasPhoto: boolean;
    latitude: number;
    longitude: number;
    bearing: number;
}

export interface PublicApiSenderSummaryResponse {
    name: string;
}

export interface PublicApiReceiverResponse {
    companyName: string;
    contactNumber: string;
    address: PublicApiAddressResponse;
}

export interface PublicApiAddressResponse {
    unitOrFlat: string;
    street: string;
    suburb: string;
    state: string;
    postcode: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    status: AddressStatus;
}

export enum AddressStatus {
    INVALID = "INVALID",
    VALIDATED = "VALIDATED"
}

export enum DimensionUnit {
    M = "M",
    CM = "CM",
    MM = "MM"
}

export enum WeightUnit {
    TN = "TN",
    KG = "KG",
    GM = "GM",
}

export enum UnitOfMeasureType {
    QUANTITY_EACH = "QUANTITY_EACH",
    VOLUME_M3 = "VOLUME_M3",
    WEIGHT_TON = "WEIGHT_TON",
    HOURS = "HOURS",
    LOADS = "LOADS",
    PALLET = "PALLET",
    CARTON = "CARTON",
    METRES_SQUARED = "METRES_SQUARED",
    ITEM = "ITEM",
    CONTAINER = "CONTAINER",
    CRATE = "CRATE",
    CASK = "CASK",
    ROLL = "ROLL",
    UNITS = "UNITS",
    LITRES = "LITRES",
    GENERAL_FREIGHT_ITEM = "GENERAL_FREIGHT_ITEM",
}

export enum ConsignmentImageKind {
    PICK_UP_DOCKET = "PICK_UP_DOCKET",
    COMPLETION_PHOTO = "COMPLETION_PHOTO",
    OTHER_PHOTO = "OTHER_PHOTO"
}

export interface PublicApiConsignmentFreightResponse {
    reference: string;
    description: string;
    height: number; // BigDecimal;
    width: number; // BigDecimal;
    length: number; // BigDecimal;
    dimensionUnit: DimensionUnit;
    weight: number; // BigDecimal;
    weightUnit: WeightUnit;
    unitOfMeasure: UnitOfMeasureType;
    unitOfMeasureDescription: string;
    orderedQuantity: number; // BigDecimal;
    grossQuantity: number; // BigDecimal;
    tareQuantity: number; // BigDecimal;
    netQuantity: number; // BigDecimal;
    netUnitOfMeasure: UnitOfMeasureType;
}

export interface PublicApiConsignmentImageResponse {
    id: number; // Long;
    kind: ConsignmentImageKind;
    formattedCreatedAt: string;
    comments: string;
}

export interface PublicApiDriverMessageResponse {
    id: number;
    formattedSentAt: string;
    message: string;
}

export interface PublicApiCallToActionResponse {
    enabled: boolean;
    description: string;
    text: string;
    url: string;
}

export interface PublicApiRequirementResponse {
    title: string;
}

export interface PublicApiCumulativeTotalsResponse {
    productDescription: string;
    unitOfMeasure: UnitOfMeasureType;
    unitOfMeasureDescription: string;
    totalOrderedQuantity: number; // BigDecimal;
    totalInTransitQuantity: number; // BigDecimal;
    totalDeliveredQuantity: number; // BigDecimal;
}

export interface PublicApiCumulativeTotalsByProgressResponse {
    scheduled: PublicApiCumulativeTotalsResponse[]
    inTransit: PublicApiCumulativeTotalsResponse[]
    completed: PublicApiCumulativeTotalsResponse[]
}

export interface PublicApiConsignmentResponse {
    status: ConsignmentStatus;
    reference: string;
    serviceRatingProvided: boolean;
    podAvailable: boolean;
    driverMessagingEnabled: boolean;
    deliveryFrom: OffsetDateTime;
    deliveryTo: OffsetDateTime;
    eta: LocalDateTime;
    completedAt: OffsetDateTime;
    formattedDeliveryFromTo: string;
    formattedETA?: string | null;
    formattedCompletedAt?: string | null;
    formattedCancelledAt?: string | null;
    vehicle: PublicApiVehicleSummaryResponse;
    driver: PublicApiDriverSummaryResponse;
    sender: PublicApiSenderSummaryResponse;
    receiver: PublicApiReceiverResponse;
    freight: PublicApiConsignmentFreightResponse[];
    images: PublicApiConsignmentImageResponse[];
    driverMessages: PublicApiDriverMessageResponse[];
    deliveryCompleteCallToAction: PublicApiCallToActionResponse;
    requirements: PublicApiRequirementResponse[];
    cumulativeTotals: PublicApiCumulativeTotalsResponse[];
    cumulativeTotalsByProgress: PublicApiCumulativeTotalsByProgressResponse;
    cumulativeTotalsConsignmentCount: number
}

